import { createApi } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import Cookies from "js-cookie";
import getTenantId from "../../components/Tenent";
import { showAccessDeniedModal } from "../slices/authSlice";
const getStore = () => require("../store").store;

const axiosInstance = axios.create({
  baseURL: `${process.env.REACT_APP_CORE_API_HOST}`,
  withCredentials: true,
  headers: {
    accept: `application/json`,
  },
});

const setUpInterceptors = (getState, dispatch) => {
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = Cookies.get("access_token");
      const refresh_token = Cookies.get("refresh_token");
      const tenantId = getTenantId();

      config.headers["x-tenant-id"] = tenantId;

      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
        config.headers["Refresh"] = `Bearer ${refresh_token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  axiosInstance.interceptors.response.use(
    (res) => res,
    async (err) => {
      const originalConfig = err.config;
      const errorMessage = err.response?.data?.error || "";

      if (
        err.response?.status === 401 &&
        errorMessage.includes("access token has expired") &&
        !originalConfig._retry
      ) {
        originalConfig._retry = true;
        const accessToken = Cookies.get("access_token");
        const refreshToken = Cookies.get("refresh_token");
        if (!refreshToken) {
          Cookies.remove("access_token");
          Cookies.remove("refresh_token");
          return Promise.reject(err);
        }

        try {
          // Determine the correct tenant ID
          const tenantId = getTenantId();

          const refreshHeaders = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${refreshToken}`,
            "x-tenant-id": tenantId,
          };
          // Request new tokens from `/core/refresh`
          const { data } = await axios.post(
            `${process.env.REACT_APP_AUTH_API}/core/refresh`,
            {
              access_token: accessToken,
              refresh_token: refreshToken,
            },
            { headers: refreshHeaders },
          );

          // Save new tokens in Cookies
          Cookies.set("access_token", data?.access_token, {
            secure: true,
          });
          Cookies.set("refresh_token", data?.refresh_token, {
            secure: true,
          });

          // Retry the original request with the new token
          originalConfig.headers["Authorization"] =
            `Bearer ${data?.access_token}`;
          originalConfig.headers["Refresh"] = `Bearer ${data?.refresh_token}`;
          return axiosInstance(originalConfig);
        } catch (_error) {
          // Refresh failed, clear cookies and redirect to login
          Cookies.remove("access_token");
          Cookies.remove("refresh_token");
          return Promise.reject(_error);
        }
      }

      return Promise.reject(err);
    },
  );
};

const axiosBaseQuery =
  () =>
  async (
    { url, method, body, params, ...requestOpts },
    { getState, dispatch },
  ) => {
    setUpInterceptors(getState, dispatch);

    const tenantId = getTenantId();

    const enhancedHeaders = {
      ...requestOpts.headers,
      "X-Tenant-Id": tenantId,
    };

    try {
      const axiosOptions = {
        url: url,
        method,
        data: body,
        params,
        headers: enhancedHeaders,
      };

      const result = await axiosInstance(axiosOptions);
      return { data: result.data };
    } catch (axiosError) {
      let err = axiosError;
      const errorData = axiosError.response?.data;
      const status = axiosError.response?.status;

      if (status === 403 && errorData?.error === "forbidden") {
        getStore().dispatch(showAccessDeniedModal());
      }
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

export const orgHierarchyApi = createApi({
  reducerPath: "orgHierarchyApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["OrgHierarchy"],
  endpoints: (builder) => ({
    getHierarchy: builder.query({
      query: () => ({
        url: "/taxonomies/hierarchy/", 
      }),
      providesTags: ["org"],
    }),
    getNodeDetails: builder.query({
      query: (nodeId) => ({
        url: `/taxonomies/${nodeId}/`, 
      }), 
      providesTags: ["org"],
    }),
    addNodeToHierarchy: builder.mutation({
      query: (body) => ({
        url: "/taxonomies/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["org"],
    }),
    updateNodeInHierarchy: builder.mutation({
      query: ({ nodeId, body }) => ({
        url: `/taxonomies/${nodeId}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["org"],
    }),
    deleteNodeInHierarchy: builder.mutation({
      query: (nodeId) => ({
        url: `/taxonomies/${nodeId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["org"],
    }),
    hierarchyLevels: builder.query({
      query: () => {
        return {
          url: `/taxonomies/hierarchy-levels/`
        }
      },
      providesTags: ["levels"]
    }),
    updateHierarchyLevels: builder.mutation({
      query: ({id, body}) => {
        return {
          url: `/taxonomies/hierarchy-levels/${id}/`,
          method: 'PATCH',
          body,
        }
      },
      invalidatesTags: ["levels"]
    }),
    getTenantConfig: builder.query({
      query: () => {
        return {
          url: `/setup/tenant-config`,
        }
      },
      providesTags: ['tenant-config']
    }),
    setTenantConfig: builder.mutation({
      query: (body) => {
        return {
          url: `/setup/tenant-config`,
          method: 'POST',
          body,
        }
      },
      invalidatesTags: ['tenant-config']
    }),
    getAppMetadata: builder.query({
      query: ({label}) => {
       return {
         url: `/setup/app-metadata`,
        params: {label}
       }
      }
    }),
    getConfigOptions: builder.query({
      query: (params) => ({
        url: "/setup/config-options",
        params,
      }),
    }),
  }),
});

export const {
  useAddNodeToHierarchyMutation,
  useUpdateNodeInHierarchyMutation,
  useDeleteNodeInHierarchyMutation,
  useGetHierarchyQuery,
  useLazyGetNodeDetailsQuery,
  useHierarchyLevelsQuery,
  useUpdateHierarchyLevelsMutation,
  useGetAppMetadataQuery,
  useGetTenantConfigQuery,
  useSetTenantConfigMutation,
  useGetConfigOptionsQuery,
} = orgHierarchyApi;
